import { ActionType, getType } from 'typesafe-actions';

import * as profileActions from './actions';
import {
  PhoneTypeListTypes,
  PreferredCurrencyTypes,
  RolesListTypes,
  UserDetailsTypes,
  UserSponsorPermission,
} from './types';

type UsersActionType = ActionType<typeof profileActions>;

export interface ProfileState {
  roles: {
    data: RolesListTypes[];
    loading: boolean;
  };
  userDetails: {
    data: UserDetailsTypes | null;
    loading: boolean;
  };
  phoneTypeList: {
    data: PhoneTypeListTypes[];
    loading: boolean;
  };
  block: {
    loading: boolean;
  };
  unBlock: {
    loading: boolean;
  };
  resetPw: {
    loading: boolean;
  };
  changePw: {
    loading: boolean;
  };
  preferredCurrency: PreferredCurrencyTypes | null;
  sponsorPermissions: {
    data: UserSponsorPermission[];
    loading: boolean;
    saving: boolean;
  };
}

const initialState: ProfileState = {
  roles: {
    data: [],
    loading: false,
  },
  userDetails: {
    data: null,
    loading: false,
  },
  phoneTypeList: {
    data: [],
    loading: false,
  },
  block: {
    loading: false,
  },
  unBlock: {
    loading: false,
  },
  resetPw: {
    loading: false,
  },
  changePw: {
    loading: false,
  },
  preferredCurrency: null,
  sponsorPermissions: {
    data: [],
    loading: false,
    saving: false,
  },
};

const profileReducer = (
  state: ProfileState = initialState,
  action: UsersActionType,
): ProfileState => {
  switch (action.type) {
    //roles list
    case getType(profileActions.getRoles.request):
      return {
        ...state,
        roles: {
          ...state.roles,
          loading: true,
        },
      };
    case getType(profileActions.getRoles.success):
      return {
        ...state,
        roles: {
          ...state.roles,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(profileActions.getRoles.failure):
      return {
        ...state,
        roles: {
          ...state.roles,
          loading: false,
        },
      };

    //user details
    case getType(profileActions.getUserDetail.request):
      return {
        ...state,
        userDetails: {
          ...state.userDetails,
          loading: true,
        },
      };
    case getType(profileActions.getUserDetail.success):
      return {
        ...state,
        userDetails: {
          ...state.userDetails,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(profileActions.getUserDetail.failure):
      return {
        ...state,
        userDetails: {
          ...state.userDetails,
          loading: false,
        },
      };

    //phone type list
    case getType(profileActions.getPhoneType.request):
      return {
        ...state,
        phoneTypeList: {
          ...state.phoneTypeList,
          loading: true,
        },
      };
    case getType(profileActions.getPhoneType.success):
      return {
        ...state,
        phoneTypeList: {
          ...state.phoneTypeList,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(profileActions.getPhoneType.failure):
      return {
        ...state,
        phoneTypeList: {
          ...state.phoneTypeList,
          loading: false,
        },
      };

    // Block the User
    case getType(profileActions.blockUsers.request):
      return {
        ...state,
        block: {
          ...state.block,
          loading: true,
        },
      };
    case getType(profileActions.blockUsers.success):
      return {
        ...state,
        block: {
          ...state.block,
          loading: false,
        },
      };
    case getType(profileActions.blockUsers.failure):
      return {
        ...state,
        block: {
          ...state.block,
          loading: false,
        },
      };

    // Unblock the User
    case getType(profileActions.unBlockUsers.request):
      return {
        ...state,
        unBlock: {
          ...state.unBlock,
          loading: true,
        },
      };
    case getType(profileActions.unBlockUsers.success):
      return {
        ...state,
        unBlock: {
          ...state.unBlock,
          loading: false,
        },
      };
    case getType(profileActions.unBlockUsers.failure):
      return {
        ...state,
        unBlock: {
          ...state.unBlock,
          loading: false,
        },
      };

    // Reset Password
    case getType(profileActions.resetPassword.request):
      return {
        ...state,
        resetPw: {
          ...state.resetPw,
          loading: true,
        },
      };
    case getType(profileActions.resetPassword.success):
      return {
        ...state,
        resetPw: {
          ...state.resetPw,
          loading: false,
        },
      };
    case getType(profileActions.resetPassword.failure):
      return {
        ...state,
        resetPw: {
          ...state.resetPw,
          loading: false,
        },
      };

    // Change Password
    case getType(profileActions.changePassword.request):
      return {
        ...state,
        changePw: {
          ...state.changePw,
          loading: true,
        },
      };
    case getType(profileActions.changePassword.success):
      return {
        ...state,
        changePw: {
          ...state.changePw,
          loading: false,
        },
      };
    case getType(profileActions.changePassword.failure):
      return {
        ...state,
        changePw: {
          ...state.changePw,
          loading: false,
        },
      };

    //preferred currency
    case getType(profileActions.setPreferredCurrency):
      return {
        ...state,
        preferredCurrency: action.payload.currency,
      };

    // Sponsor-dashboard access grants
    case getType(profileActions.getSponsorPermissions.request):
      return {
        ...state,
        sponsorPermissions: {
          ...state.sponsorPermissions,
          loading: true,
        },
      };
    case getType(profileActions.getSponsorPermissions.success):
      return {
        ...state,
        sponsorPermissions: {
          ...state.sponsorPermissions,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(profileActions.getSponsorPermissions.failure):
      return {
        ...state,
        sponsorPermissions: {
          ...state.sponsorPermissions,
          loading: false,
        },
      };

    case getType(profileActions.updateSponsorPermissions.request):
      return {
        ...state,
        sponsorPermissions: {
          ...state.sponsorPermissions,
          saving: true,
        },
      };
    case getType(profileActions.updateSponsorPermissions.success):
    case getType(profileActions.updateSponsorPermissions.failure):
      return {
        ...state,
        sponsorPermissions: {
          ...state.sponsorPermissions,
          saving: false,
        },
      };

    default:
      return state;
  }
};

export default profileReducer;
